import ProfileImage from '@brand/assets/images/profile.svg';
import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogActions, DialogContent, Slider, Stack } from '@mui/material';
import { ReactComponent as TrashIcon } from 'assets/icons/Trash.svg';
import { ReactComponent as UploadIcon } from 'assets/icons/Upload.svg';
import { StyledDialog } from 'common/components/StyledDialog/StyledDialog';
import { StyledDialogFormError } from 'common/components/StyledDialog/StyledDialogFormError';
import { StyledDialogTitle } from 'common/components/StyledDialog/StyledDialogTitle';
import { DialogComponentProps } from 'common/hooks/useDialog';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import { uploadFileToDocument } from 'common/utils/fileUtils';
import {
  useCurrentUserQuery,
  useGenerateFileDownloadUrlMutation,
  useUpdateUserAvatarMutation,
} from 'generated/graphql';
import { ChangeEvent, SyntheticEvent, useContext, useEffect, useRef, useState, WheelEvent } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { useTranslation } from 'react-i18next';

import { styles } from './ChangeAvatar.styles';

export type ChangeAvatarDialogProps = DialogComponentProps<void>;

export const ChangeAvatarDialog: React.FC<ChangeAvatarDialogProps> = ({ isOpen, onReject, onResolve }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error] = useState('');

  const { isAuthenticated } = useContext(AuthContext);

  const { data: currentUserData } = useCurrentUserQuery({ skip: !isAuthenticated });
  const [updateUserAvatar] = useUpdateUserAvatarMutation();

  const editorRef = useRef<AvatarEditor | null>(null);
  const [picture, setPicture] = useState({
    croppedImg: '',
    cropperOpen: true,
    img: ProfileImage,
    isFile: false,
    scale: 1,
  });

  const [getDownloadUrl] = useGenerateFileDownloadUrlMutation();

  useEffect(() => {
    if (!!currentUserData?.user?.avatar?.fullSize?.id) {
      getDownloadUrl({ variables: { fileId: currentUserData?.user?.avatar?.fullSize?.id } }).then((res) => {
        setPicture({
          ...picture,
          img: res.data?.generateFileDownloadUrl ?? '',
          isFile: true,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserData?.user?.avatar?.fullSize?.id]);

  const handleSlider = (event: Event, value: number | number[]) => {
    event.preventDefault();
    let scale = Array.isArray(value) ? value[0] : value;
    scale = Math.min(Math.max(0.125, scale), 16);
    setPicture({
      ...picture,
      scale,
    });
  };

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    let scale = picture.scale + event.deltaY * -0.01;
    // Restrict scale
    scale = Math.min(Math.max(0.125, scale), 16);
    setPicture({
      ...picture,
      scale,
    });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target?.files === null) return;
    const url = URL.createObjectURL(e.target.files[0]);
    setPicture({
      ...picture,
      cropperOpen: true,
      img: url,
      isFile: true,
      scale: 1,
    });
  };

  const handleDelete = () => {
    setPicture({
      ...picture,
      cropperOpen: true,
      img: ProfileImage,
      isFile: false,
      scale: 1,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!!editorRef.current) {
      setLoading(true);
      let fileId = null;
      if (picture.isFile) {
        const canvasScaled = editorRef.current.getImageScaledToCanvas();
        const croppedImgUrl = canvasScaled.toDataURL();
        const blob = await fetch(croppedImgUrl).then((r) => r.blob());
        const file = new File([blob], 'avatar.png', { type: 'image/png' });
        const fileResponse = await uploadFileToDocument(file, '');
        if (typeof fileResponse !== 'string' && 'fileId' in fileResponse) {
          fileId = fileResponse.fileId;
        }
      }
      await updateUserAvatar({
        refetchQueries: ['CurrentUser'],
        variables: {
          fileId,
        },
      });
      setLoading(false);
    }
    onResolve();
  };

  return (
    <StyledDialog maxWidth="sm" onClose={() => onReject()} open={isOpen}>
      <StyledDialogTitle textAlign="center">{t('settings.changeDialog.profileImage.title')}</StyledDialogTitle>
      <Box component="form" noValidate onSubmit={handleSubmit}>
        <DialogContent>
          <Stack alignItems="center" gap={1}>
            <Box onWheel={handleWheel}>
              <AvatarEditor
                border={0}
                borderRadius={100}
                color={[248, 248, 248, 1]}
                crossOrigin="anonymous"
                height={200}
                image={picture.img}
                ref={editorRef}
                rotate={0}
                scale={picture.scale}
                width={200}
              />
            </Box>
            <Slider
              aria-label="raceSlider"
              max={16}
              min={0.125}
              onChange={handleSlider}
              step={0.1}
              value={picture.scale}
            />
            <Stack direction="row" spacing={1}>
              <Button color="tertiaryButton" component="label">
                <Box sx={styles.icon}>
                  <UploadIcon height={16} width={16} />
                </Box>
                <input hidden onChange={handleFileChange} type="file" />
              </Button>
              <Button color="tertiaryButton" disabled={!picture.isFile} onClick={handleDelete}>
                <Box sx={styles.icon}>
                  <TrashIcon height={16} width={16} />
                </Box>
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="secondaryButton" onClick={() => onReject()}>
            {t('settings.changeDialog.cancel')}
          </Button>
          <LoadingButton loading={loading} type="submit">
            {t('settings.changeDialog.save')}
          </LoadingButton>
        </DialogActions>
        {!!error && !loading && <StyledDialogFormError content={error} title={t('settings.changeDialog.error')} />}
      </Box>
    </StyledDialog>
  );
};
